import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtUsd } from "@/lib/format";
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Download, Trash2, Eye, MapPin, Calendar, Receipt } from "lucide-react";
import { downloadWaybillPDF } from "@/lib/waybill";
import { toast } from "sonner";
import CustomerChatWidget from "@/components/customer/CustomerChatWidget";

/**
 * TripsPage (v2.6.5) — driver trip history with full waybill detail.
 *
 * Lists every trip the driver is associated with (driver-saved + dispatched)
 * via /api/trips/all. Each row is clickable → opens a detail modal that shows
 * the same line items as the admin waybill. The modal has Download (PDF) and
 * Delete buttons. Dispatched rides cannot be deleted from this page (admin
 * tools handle those) — only driver-saved trips.
 */
export default function TripsPage() {
  const [trips, setTrips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null); // trip object

  const reload = () => {
    Promise.all([api.get("/trips/all"), api.get("/trips/summary")])
      .then(([a, b]) => { setTrips(a.data.items || []); setSummary(b.data); })
      .catch((err) => console.warn("trips reload failed", err?.response?.status));
  };
  useEffect(() => { reload(); }, []);

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return String(s); }
  };

  const remove = async (t) => {
    if (t.kind !== "driver_trip") {
      toast.error("Dispatched rides can't be deleted here — contact admin");
      return;
    }
    if (!window.confirm(`Permanently delete trip ${t.trip_no || t.id?.slice(0,8)}?`)) return;
    try {
      await api.delete(`/trips/${t.id}`);
      toast.success("Trip deleted");
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const download = (t) => {
    downloadWaybillPDF({
      ...t,
      waybill_no: t.waybill_no || t.trip_no,
      ride_type: t.mode,
      quoted_fare: t.subtotal,
      total: t.total,
      // breakdown is already aligned with what waybill.js expects
    });
  };

  return (
    <Layout title="Trip History">
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-5" data-testid="trips-summary">
          <Box label="Today" t={summary.today.total} c={summary.today.count}/>
          <Box label="Week" t={summary.week.total} c={summary.week.count}/>
          <Box label="All Time" t={summary.all_time.total} c={summary.all_time.count}/>
        </div>
      )}

      <Card className="bg-zinc-900 border-zinc-800 divide-y divide-zinc-800">
        {trips.length === 0 && <div className="p-8 text-center text-zinc-500 text-sm" data-testid="trips-empty">No trips yet — calculate one to begin.</div>}
        {trips.map(t => (
          <button
            type="button"
            key={`${t.kind}-${t.id}`}
            onClick={() => setOpen(t)}
            className={`w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-zinc-900/70 active:bg-zinc-900 transition-colors ${t.is_test ? "opacity-60" : ""}`}
            data-testid={`trip-${t.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`uppercase text-[10px] ${t.kind === "ride" ? "border-blue-400/40 text-blue-300" : "border-amber-400/40 text-amber-400"}`}>{t.mode || (t.kind === "ride" ? "ride" : "trip")}</Badge>
                <span className="text-[10px] tracking-wider text-zinc-600 font-mono">#{t.trip_no || t.id?.slice(0,8).toUpperCase()}</span>
                {t.signed_off && <Badge variant="outline" className="border-emerald-400/40 text-emerald-400 text-[10px] uppercase">Signed off</Badge>}
                {t.dispatch_priced && <Badge variant="outline" className="border-sky-400/40 text-sky-300 text-[10px] uppercase" data-testid={`trip-${t.id}-dispatch-priced`}>Dispatch price</Badge>}
                {t.is_test && <Badge variant="outline" className="border-purple-400/40 text-purple-300 text-[10px] uppercase">TEST</Badge>}
                <span className="text-xs text-zinc-500">{fmtDate(t.created_at)}</span>
              </div>
              <div className="text-sm mt-1 truncate">
                {t.origin && t.destination ? `${t.origin} → ${t.destination}` : `${t.miles} mi · ${t.passengers || 1} pax`}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {Number(t.miles || 0).toFixed(2)} mi · {Math.round(t.minutes||0)} min · {t.passengers || 1} pax
                {t.customer_name ? ` · ${t.customer_name}` : ""}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="meter-display text-lg font-bold text-amber-400">{fmtUsd(t.total)}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.payment_method || "—"}</div>
            </div>
          </button>
        ))}
      </Card>

      {/* Detail / waybill modal */}
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <DialogContent className="bg-zinc-950 border-amber-400/30 text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="trip-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2"><Receipt className="h-5 w-5"/>Trip Details · #{open?.trip_no || open?.id?.slice(0,8).toUpperCase()}</DialogTitle>
          </DialogHeader>

          {open && (
            <div className="space-y-3 text-xs">
              <Section title="Trip Information">
                <Row k="Trip ID" v={open.id} mono/>
                <Row k="Waybill #" v={open.waybill_no || "—"} mono/>
                <Row k="Mode" v={(open.mode || "").toString().toUpperCase()}/>
                <Row k="Date" v={fmtDate(open.created_at)}/>
                {open.status && <Row k="Status" v={open.status.toUpperCase()}/>}
                <Row k="Driver" v={open.driver_name || "—"}/>
                {open.customer_name && <Row k="Customer" v={open.customer_name}/>}
                {open.customer_phone && <Row k="Phone" v={open.customer_phone}/>}
                <Row k="Origin" v={open.origin || "—"}/>
                <Row k="Destination" v={open.destination || "—"}/>
                <Row k="Distance" v={`${Number(open.miles || 0).toFixed(2)} mi`}/>
                <Row k="Duration" v={`${Math.round(open.minutes || 0)} min`}/>
                <Row k="Passengers" v={open.passengers || 1}/>
                {open.notes && <Row k="Notes" v={open.notes}/>}
              </Section>

              <Section title="Fare Breakdown">
                <BreakdownRows breakdown={open.breakdown || {}} pax={open.passengers || 1}/>
                <div className="border-t border-zinc-800 my-2"/>
                <Row k="Subtotal" v={fmtUsd(open.subtotal || 0)} bold/>
                <div className="flex items-center justify-between bg-amber-400/10 px-3 py-2 rounded mt-2">
                  <span className="text-sm font-bold uppercase tracking-wider text-amber-300">Total Paid</span>
                  <span className="text-xl font-bold text-amber-400 meter-display" data-testid="trip-detail-total">{fmtUsd(open.total)}</span>
                </div>
                {open.dispatch_priced && (
                  <div className="flex items-start gap-2 mt-2 text-[11px] text-sky-300 bg-sky-400/10 border border-sky-400/30 rounded px-3 py-2" data-testid="trip-detail-dispatch-note">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"/>
                    <span>Final price set by dispatch. This booking uses the dispatcher's confirmed fare, not the live meter amount.</span>
                  </div>
                )}
                <Row k="Payment Method" v={(open.payment_method || "—").toUpperCase()}/>
                {open.card_snapshot?.last4 && <Row k="Card" v={`${(open.card_snapshot.brand || "CARD").toUpperCase()} •••• ${open.card_snapshot.last4}`}/>}
              </Section>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setOpen(null)} className="bg-zinc-950 border-zinc-700 text-zinc-300">Close</Button>
            {open?.kind === "driver_trip" && (
              <Button
                data-testid="trip-detail-delete"
                variant="outline"
                onClick={() => remove(open)}
                className="bg-red-500/10 border-red-400/40 text-red-300 hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4 mr-1"/>Delete
              </Button>
            )}
            <Button
              data-testid="trip-detail-download"
              onClick={() => download(open)}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
            >
              <Download className="h-4 w-4 mr-1"/>Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CustomerChatWidget/>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold mb-2">{title}</div>
      {children}
    </Card>
  );
}
function Row({ k, v, mono, bold }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-0.5">
      <div className="text-zinc-500 uppercase tracking-wider text-[10px] self-center">{k}</div>
      <div className={`text-zinc-200 text-right break-words ${mono ? "font-mono text-[11px]" : ""} ${bold ? "font-bold" : ""}`}>{v}</div>
    </div>
  );
}
function BreakdownRows({ breakdown, pax }) {
  const bd = breakdown || {};
  const techFee = Number(bd.tech_fee || bd.maintenance_fee || 0);
  const techPax = bd.tech_fee_passengers || pax || 1;
  return (
    <div>
      {bd.fare_fee != null && <Row k="Fare fee" v={fmtUsd(bd.fare_fee)}/>}
      {bd.per_mile_total != null && <Row k="Mileage" v={fmtUsd(bd.per_mile_total)}/>}
      {bd.per_minute_total != null && <Row k="Minutes" v={fmtUsd(bd.per_minute_total)}/>}
      {bd.per_passenger_total != null && <Row k="Per pax" v={fmtUsd(bd.per_passenger_total)}/>}
      {bd.stops_total != null && bd.stops_total > 0 && <Row k="Stops" v={fmtUsd(bd.stops_total)}/>}
      {bd.airport_fee != null && bd.airport_fee > 0 && <Row k="Airport fee" v={fmtUsd(bd.airport_fee)}/>}
      {bd.toll_fee != null && bd.toll_fee > 0 && <Row k="Toll fee" v={fmtUsd(bd.toll_fee)}/>}
      {bd.local_franchise_fee != null && bd.local_franchise_fee > 0 && <Row k="Franchise fee" v={fmtUsd(bd.local_franchise_fee)}/>}
      {techFee > 0 && <Row k={`Technology Fee (${techPax} pax)`} v={fmtUsd(techFee)}/>}
      {bd.sales_tax != null && bd.sales_tax > 0 && <Row k={`County Tax (${bd.sales_tax_pct || ""}%)`} v={fmtUsd(bd.sales_tax)}/>}
      {bd.taxes_total != null && bd.taxes_total > 0 && !bd.sales_tax && <Row k="Taxes" v={fmtUsd(bd.taxes_total)}/>}
    </div>
  );
}
function Box({ label, t, c }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="meter-display text-2xl font-bold text-amber-400 mt-1">{fmtUsd(t)}</div>
      <div className="text-xs text-zinc-400">{c} trips</div>
    </Card>
  );
}
